import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import apiClient from "@/utils/apiClient";
import { Shield, ShieldCheck, ShieldOff, QrCode, Check, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AdminSecurity() {
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [step, setStep] = useState("idle"); // idle | setup | verify

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiClient.get('/auth/2fa/status');
      setStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch 2FA status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const startSetup = async () => {
    setError("");
    setSuccess("");
    try {
      const res = await apiClient.post('/auth/2fa/setup');
      setSetupData(res.data);
      setStep("setup");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start 2FA setup");
    }
  };

  const verifySetup = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await apiClient.post('/auth/2fa/verify-setup', { totp_code: verifyCode });
      setSuccess("2FA enabled successfully! You'll need the authenticator code on your next login.");
      setStep("idle");
      setSetupData(null);
      setVerifyCode("");
      fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid code. Try again.");
    }
  };

  const disable2FA = async () => {
    setError("");
    try {
      await apiClient.post('/auth/2fa/disable', {});
      setSuccess("2FA has been disabled.");
      setStep("idle");
      setSetupData(null);
      fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to disable 2FA");
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  const isEnabled = status?.totp_enabled;
  const isRequired = status?.requires_2fa;

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10 max-w-2xl mx-auto space-y-6" data-testid="admin-security-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-white mb-1">Security Settings</h1>
          <p className="text-white/40 text-sm">Two-Factor Authentication (2FA)</p>
        </div>

        {/* Status Card */}
        <div className={`rounded-2xl border p-6 ${isEnabled ? "bg-emerald-500/5 border-emerald-500/20" : "bg-[#161821] border-white/5"}`} data-testid="2fa-status-card">
          <div className="flex items-center gap-4 mb-4">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isEnabled ? "bg-emerald-500/10" : "bg-white/5"}`}>
              {isEnabled ? <ShieldCheck className="w-6 h-6 text-emerald-400" /> : <ShieldOff className="w-6 h-6 text-white/30" />}
            </div>
            <div>
              <h2 className="text-white font-medium text-lg">
                2FA is {isEnabled ? "Enabled" : "Disabled"}
              </h2>
              <p className="text-white/40 text-sm">
                {isEnabled
                  ? "Your account is protected with a time-based one-time password (TOTP)"
                  : isRequired
                    ? "2FA is required for your role. Please enable it now."
                    : "Add an extra layer of security to your account"
                }
              </p>
            </div>
          </div>

          {isRequired && !isEnabled && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
              <p className="text-amber-400 text-sm">2FA is required for Owner and Super Admin accounts.</p>
            </div>
          )}

          {success && (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 mb-4 flex items-center gap-2" data-testid="2fa-success">
              <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <p className="text-emerald-400 text-sm">{success}</p>
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 mb-4" data-testid="2fa-error">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Idle state — show enable/disable buttons */}
          {step === "idle" && (
            <div>
              {!isEnabled ? (
                <Button
                  onClick={startSetup}
                  className="bg-amber-500 hover:bg-amber-600 text-black rounded-xl gap-2"
                  data-testid="enable-2fa-btn"
                >
                  <Shield className="w-4 h-4" /> Enable 2FA
                </Button>
              ) : (
                <Button
                  onClick={disable2FA}
                  variant="outline"
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-xl gap-2"
                  data-testid="disable-2fa-btn"
                >
                  <ShieldOff className="w-4 h-4" /> Disable 2FA
                </Button>
              )}
            </div>
          )}

          {/* Setup — show QR code */}
          {step === "setup" && setupData && (
            <div className="space-y-5 mt-4" data-testid="2fa-setup-section">
              <div className="bg-[#0d0f17] border border-white/5 rounded-2xl p-6 text-center">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <QrCode className="w-5 h-5 text-amber-400" />
                  <h3 className="text-white font-medium">Scan this QR code</h3>
                </div>
                <p className="text-white/40 text-sm mb-4">
                  Open Google Authenticator, Authy, or any TOTP app and scan the QR code below.
                </p>
                <div className="inline-block bg-white p-3 rounded-2xl">
                  <img src={setupData.qr_code} alt="2FA QR Code" className="w-48 h-48" data-testid="2fa-qr-code" />
                </div>
                <div className="mt-4">
                  <p className="text-white/30 text-xs mb-1">Or enter this key manually:</p>
                  <code className="bg-white/5 px-3 py-1.5 rounded-lg text-amber-400 text-sm font-mono select-all" data-testid="2fa-secret-key">
                    {setupData.secret}
                  </code>
                </div>
              </div>

              {/* Verification input */}
              <form onSubmit={verifySetup} className="space-y-3" data-testid="2fa-verify-form">
                <label className="text-white/60 text-sm">Enter the 6-digit code from your app to verify:</label>
                <div className="flex gap-3">
                  <Input
                    type="text"
                    placeholder="000000"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="bg-[#0d0f17] border-white/10 text-white text-center text-xl tracking-[0.5em] font-mono rounded-xl flex-1"
                    maxLength={6}
                    autoFocus
                    data-testid="2fa-verify-code-input"
                  />
                  <Button
                    type="submit"
                    disabled={verifyCode.length !== 6}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl px-6"
                    data-testid="2fa-verify-setup-btn"
                  >
                    Verify
                  </Button>
                </div>
              </form>

              <button
                onClick={() => { setStep("idle"); setSetupData(null); setError(""); }}
                className="text-white/40 text-sm hover:text-white transition-colors"
                data-testid="2fa-cancel-setup"
              >
                Cancel setup
              </button>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="bg-[#161821] border border-white/5 rounded-2xl p-5">
          <h3 className="text-white/60 font-medium text-sm mb-3">How 2FA works</h3>
          <ul className="space-y-2 text-white/40 text-sm">
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-400 text-xs flex-shrink-0 mt-0.5">1</span>
              You log in with your email and password as usual
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-400 text-xs flex-shrink-0 mt-0.5">2</span>
              You're prompted for a 6-digit code from your authenticator app
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-400 text-xs flex-shrink-0 mt-0.5">3</span>
              Only after both steps is access granted — even if someone knows your password
            </li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
}
